import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, FileText, Loader2 } from "lucide-react";

import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import api from "../lib/api";
import { useWizard } from "../state/wizard";

const FIELD_LABELS = {
  project_name: "Project Name",
  client_name: "Client",
  country: "Country",
  project_type: "Project Type",
  segment: "Segment",
  architecture: "Architecture",
};

export default function ReviewPage() {
  const nav = useNavigate();
  const { inputs, setResult } = useWizard();
  const [schema, setSchema] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!inputs.segment || !inputs.architecture) {
      nav("/wizard");
    }
  }, [inputs, nav]);

  useEffect(() => {
    api.get("/spec/schema").then((r) => setSchema(r.data));
  }, []);

  const appNameMap = useMemo(() => {
    if (!schema) return {};
    return Object.fromEntries(schema.applications.map((a) => [a.slug, a.name]));
  }, [schema]);
  const featNameMap = useMemo(() => {
    if (!schema) return {};
    return Object.fromEntries(
      [...schema.functional_requirements, ...schema.digital_services].map((f) => [
        f.slug,
        f.name,
      ])
    );
  }, [schema]);

  const generate = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api.post("/spec/generate", { inputs });
      setResult(r.data);
      nav("/results");
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="review-page" className="min-h-screen flex flex-col bg-white">
      <Header />
      <div className="max-w-4xl w-full mx-auto px-6 lg:px-8 pt-12 pb-6">
        <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#c80f3f]">
          Review · Step 07 / 07
        </div>
        <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold text-[#1f2a44] tracking-tight">
          Confirm your inputs
        </h1>
        <p className="mt-3 text-sm text-[#4b5563] max-w-2xl leading-relaxed">
          Review the summary below. When you generate, the engine will apply the
          approved clause selection logic and assemble the specification.
        </p>
      </div>

      <div className="flex-1 max-w-4xl w-full mx-auto px-6 lg:px-8 py-8 space-y-6">
        <Card className="border border-[#e5e7eb] rounded-sm shadow-none">
          <div className="p-6 sm:p-8">
            <SectionTitle>Project details</SectionTitle>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
              {["project_name", "client_name", "country", "project_type"].map((k) => (
                <Row key={k} label={FIELD_LABELS[k]} value={inputs[k] || "-"} />
              ))}
            </dl>

            <div className="my-8 h-px bg-[#e5e7eb]" />

            <SectionTitle>Segment & architecture</SectionTitle>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
              <Row label="Segment" value={inputs.segment} accent />
              <Row label="Architecture" value={inputs.architecture} accent />
            </dl>

            <div className="my-8 h-px bg-[#e5e7eb]" />

            <SectionTitle>Applications</SectionTitle>
            <div className="flex flex-wrap gap-2">
              {(inputs.applications || []).length === 0 && (
                <span className="text-sm text-[#4b5563]">None selected.</span>
              )}
              {(inputs.applications || []).map((slug) => (
                <span
                  key={slug}
                  data-testid={`review-app-${slug}`}
                  className="text-xs font-medium px-2.5 py-1 border border-[#1f2a44] text-[#1f2a44] rounded-sm"
                >
                  {appNameMap[slug] || slug}
                </span>
              ))}
            </div>

            <div className="my-8 h-px bg-[#e5e7eb]" />

            <SectionTitle>Functional requirements</SectionTitle>
            <TagList
              items={(inputs.functional_requirements || []).map(
                (s) => featNameMap[s] || s
              )}
              testId="review-functional"
            />

            <div className="my-8 h-px bg-[#e5e7eb]" />

            <SectionTitle>Digital services</SectionTitle>
            <TagList
              items={(inputs.digital_services || []).map((s) => featNameMap[s] || s)}
              testId="review-digital"
            />
          </div>
        </Card>

        {err && (
          <div className="text-sm text-[#c80f3f] border border-[#c80f3f]/40 bg-[#c80f3f]/5 rounded-sm px-4 py-3">
            {err}
          </div>
        )}

        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            onClick={() => nav("/wizard")}
            data-testid="review-back-button"
            className="text-[#1f2a44] hover:bg-[#f4f5f7] rounded-sm"
          >
            <ArrowLeft className="mr-2 h-4 w-4" strokeWidth={1.75} />
            Back to wizard
          </Button>
          <Button
            onClick={generate}
            disabled={busy}
            data-testid="generate-spec-button"
            className="bg-[#c80f3f] hover:bg-[#a10c32] text-white rounded-sm px-8 py-6 text-base"
          >
            {busy ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.75} />
                Assembling…
              </>
            ) : (
              <>
                <FileText className="mr-2 h-4 w-4" strokeWidth={1.75} />
                Generate Specification
                <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.75} />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#4b5563] mb-4">
      {children}
    </div>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="flex items-baseline justify-between gap-6 border-b border-dashed border-[#e5e7eb] py-2 sm:border-none sm:py-0">
      <dt className="text-xs uppercase tracking-wider text-[#4b5563]">{label}</dt>
      <dd
        className={`text-sm font-medium ${
          accent ? "text-[#c80f3f]" : "text-[#1f2937]"
        }`}
      >
        {value || "-"}
      </dd>
    </div>
  );
}

function TagList({ items, testId }) {
  if (!items.length)
    return <span className="text-sm text-[#4b5563]">None selected.</span>;
  return (
    <div className="flex flex-wrap gap-2" data-testid={testId}>
      {items.map((it) => (
        <span
          key={it}
          className="text-xs font-medium px-2.5 py-1 bg-[#f4f5f7] text-[#1f2937] rounded-sm border border-[#e5e7eb]"
        >
          {it}
        </span>
      ))}
    </div>
  );
}
